/**
 * useUserProfile
 *
 * Combines authentication data with Microsoft Graph API data to build
 * a richer user profile including profile photo and organizational context.
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from './useAuth';
import { useGraphAPI } from './useGraphAPI';
import type { GraphUser } from '../services/graphClient';

export interface UserProfile {
  userId: string;
  email: string;
  displayName: string;
  firstName?: string;
  lastName?: string;
  jobTitle?: string;
  department?: string;
  officeLocation?: string;
  city?: string;
  state?: string;
  country?: string;
  businessPhones?: string[];
  photoUrl?: string | null;
  isGraphDataLoaded: boolean;
  isPhotoLoaded: boolean;
}

interface UseUserProfileReturn {
  profile: UserProfile | null;
  loading: boolean;
  error: string | null;
  refreshProfile: () => Promise<void>;
  refreshPhoto: () => Promise<void>;
}

// ---------------------------------------------------------------------------
// Local-dev only: default user location so location-aware agents (e.g.
// PolicyTech state-specific policies) have something to work with when Graph
// doesn't return a state on localhost. Defaults to Minnesota. Override in
// the browser console:
//   window.askAldo.setDevLocation({ state: 'NY', city: 'New York' })
//   window.askAldo.clearDevLocation()
// ---------------------------------------------------------------------------
const DEV_LOCATION_KEY = 'askaldo_dev_location';
const DEV_DEFAULT_LOCATION = {
  state: 'MN',
  city: 'Minneapolis',
  country: 'United States',
  officeLocation: 'Minneapolis, MN',
};

const isLocalhostEnv = (): boolean => {
  if (typeof window === 'undefined') return false;
  const h = window.location.hostname;
  return (
    h === 'localhost' ||
    h === '127.0.0.1' ||
    h.startsWith('192.168.') ||
    h.endsWith('.local')
  );
};

const getDevLocation = (): Partial<UserProfile> => {
  if (typeof window === 'undefined') return DEV_DEFAULT_LOCATION;
  try {
    const raw = window.localStorage.getItem(DEV_LOCATION_KEY);
    if (raw) return { ...DEV_DEFAULT_LOCATION, ...JSON.parse(raw) };
  } catch {
    /* ignore malformed override */
  }
  return DEV_DEFAULT_LOCATION;
};

if (typeof window !== 'undefined' && isLocalhostEnv()) {
  (window as any).askAldo = (window as any).askAldo || {};
  (window as any).askAldo.setDevLocation = (loc: Partial<UserProfile>) => {
    window.localStorage.setItem(DEV_LOCATION_KEY, JSON.stringify(loc));
    console.log('✅ Dev location set (refresh to apply):', { ...DEV_DEFAULT_LOCATION, ...loc });
  };
  (window as any).askAldo.clearDevLocation = () => {
    window.localStorage.removeItem(DEV_LOCATION_KEY);
    console.log('✅ Dev location cleared (back to default MN on refresh).');
  };
}

export const useUserProfile = (autoLoad: boolean = true): UseUserProfileReturn => {
  const {
    user,
    isAuthenticated,
    isLoading: authLoading,
    getUserDisplayName,
    getUserEmail,
  } = useAuth();

  const {
    currentUser: graphUser,
    currentUserPhoto: graphPhoto,
    loading: graphLoading,
    error: graphError,
    fetchCurrentUserProfile,
    fetchCurrentUserPhoto,
    clearError,
  } = useGraphAPI();

  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [hasAttemptedLoad, setHasAttemptedLoad] = useState(false);

  const buildProfile = useCallback(
    (graphData?: GraphUser | null, photoUrl?: string | null): UserProfile | null => {
      if (!user) return null;

      const displayName = graphData?.displayName || getUserDisplayName();
      const email = graphData?.mail || getUserEmail() || user.userDetails;

      const nameParts = displayName.split(' ');
      const firstName = graphData?.givenName || nameParts[0] || displayName;
      const lastName = graphData?.surname || (nameParts.length > 1 ? nameParts.slice(1).join(' ') : '');

      // Local-dev fallback: if Graph didn't return a state on localhost,
      // inject a default dev location (Minnesota) so location-aware agents
      // have something to work with. No effect outside localhost.
      const devLoc = isLocalhostEnv() && !graphData?.state ? getDevLocation() : {};

      return {
        userId: user.userId,
        email,
        displayName,
        firstName,
        lastName,
        jobTitle: graphData?.jobTitle,
        department: graphData?.department,
        officeLocation: graphData?.officeLocation || devLoc.officeLocation,
        city: graphData?.city || devLoc.city,
        state: graphData?.state || devLoc.state,
        country: graphData?.country || devLoc.country,
        businessPhones: graphData?.businessPhones,
        photoUrl: photoUrl !== undefined ? photoUrl : null,
        isGraphDataLoaded: !!graphData,
        isPhotoLoaded: photoUrl !== undefined,
      };
    },
    [user, getUserDisplayName, getUserEmail]
  );

  const refreshProfile = useCallback(async () => {
    if (!isAuthenticated || authLoading) return;

    try {
      clearError();
      const graphData = await fetchCurrentUserProfile();
      setProfile((prev) => buildProfile(graphData, prev?.photoUrl));
    } catch (err) {
      console.error('Failed to refresh user profile:', err);
      setProfile((prev) => buildProfile(null, prev?.photoUrl));
    }
  }, [isAuthenticated, authLoading, fetchCurrentUserProfile, buildProfile, clearError]);

  const refreshPhoto = useCallback(async () => {
    if (!isAuthenticated || authLoading) return;

    try {
      clearError();
      const photoUrl = await fetchCurrentUserPhoto();
      setProfile((prev) => (prev ? { ...prev, photoUrl, isPhotoLoaded: true } : null));
    } catch (err) {
      console.error('Failed to refresh user photo:', err);
      setProfile((prev) => (prev ? { ...prev, photoUrl: null, isPhotoLoaded: true } : null));
    }
  }, [isAuthenticated, authLoading, fetchCurrentUserPhoto, clearError]);

  useEffect(() => {
    if (!autoLoad || !isAuthenticated || authLoading || hasAttemptedLoad) return;

    setHasAttemptedLoad(true);
    setProfile(buildProfile(null, undefined));

    const loadGraphData = async () => {
      try {
        const [graphData, photoUrl] = await Promise.all([
          fetchCurrentUserProfile().catch((err) => {
            console.warn('Graph profile fetch failed:', err?.message || err);
            return null;
          }),
          fetchCurrentUserPhoto().catch((err) => {
            console.warn('Graph photo fetch failed:', err?.message || err);
            return null;
          }),
        ]);

        setProfile(buildProfile(graphData, photoUrl));
      } catch (err) {
        console.error('Failed to load Graph data:', err);
      }
    };

    loadGraphData();
  }, [
    autoLoad,
    isAuthenticated,
    authLoading,
    hasAttemptedLoad,
    buildProfile,
    fetchCurrentUserProfile,
    fetchCurrentUserPhoto,
  ]);

  useEffect(() => {
    if (graphUser || graphPhoto !== undefined) {
      setProfile(buildProfile(graphUser, graphPhoto));
    }
  }, [graphUser, graphPhoto, buildProfile]);

  return {
    profile,
    loading: authLoading || graphLoading,
    error: graphError,
    refreshProfile,
    refreshPhoto,
  };
};

export default useUserProfile;



